// CameraCapture.js
// Pantalla propia (fixed overlay) para tomar una foto o filmar un video con
// la cámara del dispositivo usando getUserMedia, igual que QRScanner.js —
// ese enfoque abre la cámara nativa de forma confiable, a diferencia de
// <input type="file" capture> que en muchos WebView termina abriendo la
// galería en vez de la cámara.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Typography, Box, Alert, IconButton, CircularProgress, Button } from '@mui/material';
import { Close as CloseIcon } from '../icons';
import { Refresh as RefreshIcon } from '../icons';
import { FiberManualRecord as FiberManualRecordIcon } from '../icons';
import { Stop as StopIcon } from '../icons';
import { PhotoCamera as PhotoCameraIcon } from '../icons';
import { T } from '../routes/Translate';

const CameraCapture = ({ mode, onCapture, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [cameraError, setCameraError] = useState(null);
  const [isStarting, setIsStarting] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let stream = null;
    // Un intento viejo (React StrictMode monta el efecto dos veces, o un
    // "Reintentar" rápido) no debe pisar el estado del intento vigente: su
    // play() se aborta al cambiar el srcObject y mostraba "No se pudo
    // acceder a la cámara" aunque la cámara ya estaba andando.
    let cancelled = false;
    const videoEl = videoRef.current;

    const startCamera = async () => {
      setIsStarting(true);
      setCameraError(null);
      try {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment' },
            audio: mode === 'video',
          });
        } catch (err) {
          // Si falla pidiendo cámara + micrófono juntos (por ejemplo, el
          // permiso de micrófono nunca fue declarado/otorgado en la app),
          // reintentamos solo con video: mejor grabar sin audio que no
          // poder grabar nada.
          if (mode !== 'video') throw err;
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment' },
          });
        }
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setIsStarting(false);
        }
      } catch (err) {
        if (cancelled) return;
        setIsStarting(false);
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setCameraError(<T>No diste permiso para usar la cámara. Habilitalo desde la configuración del navegador e intentá de nuevo.</T>);
        } else if (err.name === 'NotFoundError' || err.name === 'OverconstrainedError') {
          setCameraError(<T>No encontramos ninguna cámara disponible en este dispositivo.</T>);
        } else {
          setCameraError(<T>No se pudo acceder a la cámara.</T>);
        }
      }
    };
    startCamera();
    return () => {
      cancelled = true;
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (videoEl) {
        videoEl.srcObject = null;
      }
    };
  }, [mode, retryKey]);

  const handleRetry = useCallback(() => {
    setRetryKey((key) => key + 1);
  }, []);

  const handleTakePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture(file);
      },
      'image/jpeg',
      0.9
    );
  };

  const handleStartRecording = () => {
    const stream = videoRef.current?.srcObject;
    if (!stream) return;
    chunksRef.current = [];
    const hasAudio = stream.getAudioTracks().length > 0;
    const preferredMimeType = hasAudio ? 'video/webm;codecs=vp8,opus' : 'video/webm;codecs=vp8';
    const mimeType = MediaRecorder.isTypeSupported(preferredMimeType) ? preferredMimeType : 'video/webm';
    const recorder = new MediaRecorder(stream, { mimeType });
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const file = new File([blob], `video_${Date.now()}.webm`, { type: mimeType });
      onCapture(file);
    };
    recorder.start();
    mediaRecorderRef.current = recorder;
    setIsRecording(true);
  };

  const handleStopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  return (
    <Box sx={{ position: 'fixed', zIndex: 1300, top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(24,26,27,0.98)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <IconButton
        onClick={() => {
          if (isRecording) handleStopRecording();
          onClose();
        }}
        sx={{ position: 'absolute', top: 16, right: 16, color: 'white', zIndex: 1400 }}
      >
        <CloseIcon />
      </IconButton>

      <Box sx={{ width: '100%', maxWidth: 400, mb: 2 }}>
        <Typography variant="h5" align="center" color="white" gutterBottom>
          {mode === 'video' ? <T>Filmar video</T> : <T>Tomar foto</T>}
        </Typography>
      </Box>

      {cameraError ? (
        <Box sx={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Alert severity="error" sx={{ width: '100%' }}>
            {cameraError}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRetry}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
          >
            <T>Reintentar</T>
          </Button>
        </Box>
      ) : (
        <>
          <Box sx={{ position: 'relative', width: '100%', maxWidth: 400, aspectRatio: '3/4', mb: 3 }}>
            <video
              ref={videoRef}
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12, background: '#000' }}
              autoPlay
              playsInline
              muted
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            {isStarting && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  borderRadius: '12px',
                  bgcolor: 'rgba(0,0,0,0.6)',
                }}
              >
                <CircularProgress sx={{ color: 'white' }} size={32} />
                <Typography variant="body2" color="white">
                  <T>Iniciando cámara...</T>
                </Typography>
              </Box>
            )}
            {isRecording && (
              <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(0,0,0,0.5)', borderRadius: '999px', px: 1.2, py: 0.4 }}>
                <FiberManualRecordIcon sx={{ color: '#ff3b30', fontSize: 14 }} />
                <Typography variant="caption" color="white"><T>Grabando</T></Typography>
              </Box>
            )}
          </Box>

          {!isStarting && (
            mode === 'video' ? (
              <IconButton
                onClick={isRecording ? handleStopRecording : handleStartRecording}
                sx={{
                  width: 72,
                  height: 72,
                  bgcolor: isRecording ? '#ff3b30' : 'white',
                  '&:hover': { bgcolor: isRecording ? '#e0342b' : '#eee' },
                }}
              >
                {isRecording ? <StopIcon sx={{ color: 'white', fontSize: 32 }} /> : <FiberManualRecordIcon sx={{ color: '#ff3b30', fontSize: 40 }} />}
              </IconButton>
            ) : (
              <IconButton
                onClick={handleTakePhoto}
                sx={{ width: 72, height: 72, bgcolor: 'white', '&:hover': { bgcolor: '#eee' } }}
              >
                <PhotoCameraIcon sx={{ color: '#18191b', fontSize: 34 }} />
              </IconButton>
            )
          )}
        </>
      )}
    </Box>
  );
};

export default CameraCapture;
